import { Box, CircularProgress, useTheme } from '@mui/material';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import { useGet } from 'hooks/use-get';
import { AlertTriangle, CheckCircle, Pen } from 'lucide-react';
import Mui from 'materials';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory, useParams } from 'react-router-dom';
import titleCase from 'utils/title-case';
import { getBluViuNutritionCalibrationStatus } from 'views/bluviu-nutrition/features/calibrations/api/r2-calibration';
import { getSimpleCalibrationStatus } from 'views/bluviu-nutrition/features/calibrations/api/simple-calibration';
import CloneCalibrationDialog from 'views/bluviu-nutrition/features/calibrations/components/clone-calibration-dialog';
import DeleteCalibrationDialog from 'views/bluviu-nutrition/features/calibrations/components/delete-calibration-dialog';
import { CalibrationModel } from 'views/bluviu-nutrition/features/calibrations/types/calibration-model.enum';
import { useSelectedSite } from 'views/bluviu-nutrition/hooks/use-selected-site';
import { CalibrationListItem } from '../types/calibration-list-item.type';
import { CalibrationState } from '../types/calibration-state.enum';
import { CalibrationsParams } from '../types/calibrations-params.type';
import { getCalibrationType } from '../utils/get-calibration-type';

const PAGE_NAME_ID = `bluViuNutrition.calibrations.listCalibrations`;

const ListCalibrationRow: React.FC<{ calibration: CalibrationListItem }> = ({ calibration }) => {
  const history = useHistory();
  const theme = useTheme();
  const { selectedSite } = useSelectedSite();
  const params = useParams<CalibrationsParams>();
  const queryClient = useQueryClient();

  const [refetchInterval, setRefetchInterval] = useState<number | false | undefined>(false);
  const [updatedCalibrationStatus, setUpdatedCalibrationStatus] = useState<boolean>(false); // if true, render a check indicating a calibration that was running is now completed
  const previousCalibrationRunning = useRef<boolean | undefined>();

  const { data: calibrationStatus } = useGet(
    ['getCalibrationStatus', params.globalSiteId, calibration.id],
    ({ queryKey }) =>
      calibration.model === CalibrationModel.EFFECTIVE_ENERGY_GT2_MULTI_CAGE
        ? getBluViuNutritionCalibrationStatus(String(queryKey[1]), String(queryKey[2]))
        : getSimpleCalibrationStatus(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: calibration.state === CalibrationState.SUBMITTED,
      refetchInterval: refetchInterval,
    }
  );

  useEffect(() => {
    if (!calibrationStatus) return;

    if (previousCalibrationRunning.current === true && !calibrationStatus.calibrationIsRunning) {
      queryClient.invalidateQueries({
        queryKey: ['getCalibrations', params.globalSiteId],
        exact: true,
      });

      setUpdatedCalibrationStatus(true); // the calibration status has finished successfully, set to true and render a checkbox
    }
    previousCalibrationRunning.current = calibrationStatus.calibrationIsRunning;
    setRefetchInterval(calibrationStatus.calibrationIsRunning ? 3000 : false);
  }, [calibrationStatus, params.globalSiteId, queryClient]);

  const handleOnClick = () => {
    history.push({
      pathname: `/bluviu-nutrition/calibrations/${params.globalSiteId}/${getCalibrationType(calibration.model)}/${calibration.id}/`,
      state: { siteName: selectedSite?.name },
    });
  };

  return (
    <ListRow onClick={() => handleOnClick()}>
      <ListCell>
        <Box display={'flex'} flexDirection={'row'} gap={4}>
          <Box display={'flex'} alignItems={'center'}>
            {updatedCalibrationStatus && calibration.state === CalibrationState.COMPLETED ? (
              <CheckCircle size={18} style={{ alignSelf: 'center' }} />
            ) : (
              calibration.state === CalibrationState.SUBMITTED &&
              calibrationStatus?.calibrationIsRunning && <CircularProgress size={18} style={{ alignSelf: 'center' }} />
            )}
          </Box>
          {calibration.name}
        </Box>
      </ListCell>
      <ListCell>{titleCase(calibration.model)}</ListCell>
      <ListCell>
        {!calibration.calibratedAt ? (
          '-'
        ) : (
          <FormattedDate value={calibration.calibratedAt} day="2-digit" month="short" year="numeric" />
        )}
      </ListCell>
      <ListCell>
        <Box display={'flex'} flexDirection={'row'} gap={4} alignItems={'center'}>
          {titleCase(calibration.state)}
          {calibration.state === CalibrationState.MODIFIED && (
            <Mui.Tooltip arrow title={<FormattedMessage id={`${PAGE_NAME_ID}.stateTooltip.modified`} />}>
              <AlertTriangle size={22} color={theme.palette.warning.light} />
            </Mui.Tooltip>
          )}
        </Box>
      </ListCell>
      <ListCell>
        <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'}>
          <CloneCalibrationDialog
            calibration={{
              name: calibration.name,
              id: calibration.id,
              model: calibration.model,
            }}
          />
          <DeleteCalibrationDialog
            calibration={{
              name: calibration.name,
              id: calibration.id,
              model: calibration.model,
              state: calibration.state,
            }}
          />
          <Mui.Tooltip
            title={
              <FormattedMessage
                id={`general.edit`}
                values={{
                  type: '',
                }}
              />
            }>
            <span>
              <Mui.IconButton onClick={() => handleOnClick()}>
                <Pen size={22} />
              </Mui.IconButton>
            </span>
          </Mui.Tooltip>
        </Box>
      </ListCell>
    </ListRow>
  );
};

export default React.memo(ListCalibrationRow);
